import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  WatchQueryFetchPolicy,
  ApolloLink,
  ObservableQuery,
} from '@apollo/client';
import {
  Column,
  CountTableRowsRequest,
  Data,
  DataColumn,
  DataRowsResponse,
  DataType,
  DeleteDataRowsRequest,
  DownloadDataToFileRequest,
  DownloadDataToFileResponse,
  FindDataRowsRequest,
  FindRowLocksRequest,
  FindUniqueColumnRequest,
  FindUniqueTableRequest,
  InsertDataFromFileRequest,
  LockDataRowsByFilterRequest,
  LockDataRowsRequest,
  LockDataRowsResponse,
  LockedFilterType,
  QueryTableRequest,
  QueryTableResponse,
  RowData,
  RowLock,
  Table,
  UnlockDataRowsByFilterRequest,
  UnlockDataRowsRequest,
  UpdateDataColumnValuesRequest,
} from '@ih/app/shared/apis/interfaces';
import { Store } from '@ngxs/store';
import { Apollo, gql } from 'apollo-angular';
import moment from 'moment';
import * as Papa from 'papaparse';
import { lastValueFrom, map, Observable, take } from 'rxjs';
import {
  CopyDataRowsRequest,
  CreateRowCopyMappingRequest,
  DeleteRowCopyMappingRequest,
  FindAllRowCopyMappingsRequest,
  RowCopyMapping,
  UpdateRowCopyMappingRequest,
} from './row-copy.types';

@Injectable({ providedIn: 'root' })
export class TablesService {
  private readonly apollo = inject(Apollo);
  private readonly http = inject(HttpClient);
  private readonly store = inject(Store);

  /*** graphql queries and mutations ***/
  findTable(request: FindUniqueTableRequest): Observable<Table | null> {
    type ResultType = { findUniqueTable: Table | null };

    let fetchPolicy = 'cache-only';

    const networkStatus = this.store.selectSnapshot<boolean>(
      (state) => state.network.status,
    );

    if (networkStatus) {
      fetchPolicy = 'network-only';
    }

    const query = gql`
      query findUniqueTable($request: FindUniqueTableRequest!) {
        findUniqueTable(request: $request) {
          name
          id
        }
      }
    `;

    return this.apollo
      .watchQuery<ResultType>({
        query,
        variables: {
          request,
        },
        fetchPolicy: <WatchQueryFetchPolicy>fetchPolicy,
      })
      .valueChanges.pipe(
        take(1),
        map((result: ObservableQuery.Result<ResultType>) => {
          if (result.data && networkStatus) {
            this.apollo.client.cache.writeQuery({
              query,
              data: result.data,
            });
          }
          return (
            (result.data?.findUniqueTable as Table | null | undefined) ?? null
          );
        }),
      );
  }

  findColumn(request: FindUniqueColumnRequest): Observable<Column | null> {
    type ResultType = { findUniqueColumn: Column | null };

    let fetchPolicy = 'cache-only';

    const networkStatus = this.store.selectSnapshot<boolean>(
      (state) => state.network.status,
    );

    if (networkStatus) {
      fetchPolicy = 'network-only';
    }

    const query = gql`
      query findUniqueColumn($request: FindUniqueColumnRequest!) {
        findUniqueColumn(request: $request) {
          name
          id
          dataType
        }
      }
    `;

    return this.apollo
      .watchQuery<ResultType>({
        query,
        variables: {
          request,
        },
        fetchPolicy: <WatchQueryFetchPolicy>fetchPolicy,
      })
      .valueChanges.pipe(
        take(1),
        map((result: ObservableQuery.Result<ResultType>) => {
          if (result.data && networkStatus) {
            this.apollo.client.cache.writeQuery({
              query,
              data: result.data,
            });
          }
          return (
            (result.data?.findUniqueColumn as Column | null | undefined) ?? null
          );
        }),
      );
  }

  findDataRows(
    request: FindDataRowsRequest,
  ): Observable<DataRowsResponse | null> {
    type ResultType = { findDataRows: DataRowsResponse };
    let fetchPolicy = 'cache-only';
    const networkStatus = this.store.selectSnapshot<boolean>(
      (state) => state.network.status,
    );

    if (networkStatus) {
      fetchPolicy = 'network-only';
    }

    const query = gql`
      query FindDataRows(
        $tableId: String!
        $cursor: String
        $limit: Int!
        $orderBy: String!
        $orderByType: DataType!
        $search: String
        $lockedFilter: LockedFilterType
      ) {
        findDataRows(
          request: {
            tableId: $tableId
            cursor: $cursor
            limit: $limit
            orderBy: $orderBy
            orderByType: $orderByType
            search: $search
            lockedFilter: $lockedFilter
          }
        ) {
          rows {
            columns {
              columnId
              value
            }
          }
          cursor
          locks {
            id
            tableId
            primaryKeyValue
            lockedById
            lockedAt
            reason
          }
        }
      }
    `;

    return this.apollo
      .watchQuery<ResultType>({
        query,
        variables: {
          tableId: request.tableId,
          cursor: request.cursor,
          limit: request.limit,
          orderBy: request.orderBy,
          orderByType: request.orderByType,
          search: request.search,
          lockedFilter: request.lockedFilter,
        },
        fetchPolicy: <WatchQueryFetchPolicy>fetchPolicy,
      })
      .valueChanges.pipe(
        take(1),
        map((result: ObservableQuery.Result<ResultType>) => {
          if (result.data && networkStatus) {
            this.apollo.client.cache.writeQuery({
              query,
              data: result.data,
            });
          }
          return (
            (result.data?.findDataRows as
              | DataRowsResponse
              | null
              | undefined) ?? null
          );
        }),
      );
  }

  queryTable(
    request: QueryTableRequest,
  ): Observable<(RowData | null)[] | null> {
    // Array can be null or array can contain null elements
    type ResultType = { queryTable: QueryTableResponse };
    let fetchPolicy = 'cache-only';
    const networkStatus = this.store.selectSnapshot<boolean>(
      (state) => state.network.status,
    );

    if (networkStatus) {
      fetchPolicy = 'network-only';
    }

    const query = gql`
      query QueryTable($request: QueryTableRequest!) {
        queryTable(request: $request) {
          rows {
            columns {
              key
              value
            }
          }
        }
      }
    `;

    return this.apollo
      .watchQuery<ResultType>({
        query,
        variables: {
          request: request,
        },
        fetchPolicy: <WatchQueryFetchPolicy>fetchPolicy,
      })
      .valueChanges.pipe(
        take(1),
        map((result: ObservableQuery.Result<ResultType>) => {
          if (result.data && networkStatus) {
            this.apollo.client.cache.writeQuery({
              query,
              data: result.data,
            });
          }
          return (
            (result.data?.queryTable?.rows as
              | (RowData | null)[]
              | null
              | undefined) ?? null
          );
        }),
      );
  }

  countTableRows(request: CountTableRowsRequest): Observable<number | null> {
    type ResultType = { countTableRows: { count: number } };
    let fetchPolicy = 'cache-only';
    const networkStatus = this.store.selectSnapshot<boolean>(
      (state) => state.network.status,
    );

    if (networkStatus) {
      fetchPolicy = 'network-only';
    }

    const query = gql`
      query countTableRows($request: CountTableRowsRequest!) {
        countTableRows(request: $request) {
          count
        }
      }
    `;

    return this.apollo
      .watchQuery<ResultType>({
        query,
        variables: {
          request,
        },
        fetchPolicy: <WatchQueryFetchPolicy>fetchPolicy,
      })
      .valueChanges.pipe(
        take(1),
        map((result: ObservableQuery.Result<ResultType>) => {
          if (result.data && networkStatus) {
            this.apollo.client.cache.writeQuery({
              query,
              data: result.data,
            });
          }
          if (result.data) return result.data.countTableRows!.count ?? null;
          else return null;
        }),
      );
  }

  updateDataColumnValues(
    request: UpdateDataColumnValuesRequest,
  ): Observable<Data | null> {
    type ResultType = { updateDataColumnValues: Data };
    return this.apollo
      .mutate<ResultType>({
        mutation: gql`
          mutation UpdateDataColumnValues(
            $tableId: String!
            $primaryKeyValue: String!
            $row: DataRowInput!
            $formId: String
            $formName: String
          ) {
            updateDataColumnValues(
              request: {
                tableId: $tableId
                primaryKeyValue: $primaryKeyValue
                row: $row
                formId: $formId
                formName: $formName
              }
            ) {
              projectId
            }
          }
        `,
        variables: {
          tableId: request.tableId,
          primaryKeyValue: request.primaryKeyValue,
          row: request.row,
          formId: request.formId,
          formName: request.formName,
        },
        fetchPolicy: 'network-only',
      })
      .pipe(
        map((result: Apollo.MutateResult<ResultType>) => {
          return result.data?.updateDataColumnValues || null;
        }),
      );
  }

  generateDownloadFile(
    request: DownloadDataToFileRequest,
  ): Observable<DownloadDataToFileResponse | null> {
    type ResultType = { downloadDataToFile: DownloadDataToFileResponse };
    let fetchPolicy = 'cache-only';
    const networkStatus = this.store.selectSnapshot<boolean>(
      (state) => state.network.status,
    );

    if (networkStatus) {
      fetchPolicy = 'network-only';
    }

    const query = gql`
      query downloadDataToFile($request: DownloadDataToFileRequest!) {
        downloadDataToFile(request: $request) {
          response
        }
      }
    `;
    return this.apollo
      .watchQuery<ResultType>({
        query,
        variables: {
          request,
        },
        fetchPolicy: <WatchQueryFetchPolicy>fetchPolicy,
      })
      .valueChanges.pipe(
        take(1),
        map((result: ObservableQuery.Result<ResultType>) => {
          if (result.data && networkStatus) {
            this.apollo.client.cache.writeQuery({
              query,
              data: result.data,
            });
          }
          return result.data?.downloadDataToFile || null;
        }),
      );
  }

  insertDataFromFile(
    request: InsertDataFromFileRequest,
  ): Observable<Data | null> {
    type ResultType = { insertDataFromFile: Data };

    const response = this.apollo
      .mutate<ResultType>({
        mutation: gql`
          mutation InsertDataFromFile(
            $tableId: String!
            $filePath: String!
            $email: String!
          ) {
            insertDataFromFile(
              request: { tableId: $tableId, filePath: $filePath, email: $email }
            ) {
              projectId
              rowsAffected
            }
          }
        `,
        variables: {
          tableId: request.tableId,
          filePath: request.filePath,
          email: request.email,
        },
        fetchPolicy: 'network-only',
      })
      .pipe(
        map((result: Apollo.MutateResult<ResultType>) => {
          return result.data?.insertDataFromFile || null;
        }),
      );

    return response;
  }

  deleteDataRows(request: DeleteDataRowsRequest): Observable<Data | null> {
    type ResultType = { deleteDataRows: Data };

    return this.apollo
      .mutate<ResultType>({
        mutation: gql`
          mutation DeleteDataRows(
            $tableId: String!
            $primaryKeyValues: [String!]!
            $deleteFiles: Boolean
          ) {
            deleteDataRows(
              request: {
                tableId: $tableId
                primaryKeyValues: $primaryKeyValues
                deleteFiles: $deleteFiles
              }
            ) {
              projectId
            }
          }
        `,
        variables: {
          tableId: request.tableId,
          primaryKeyValues: request.primaryKeyValues,
          deleteFiles: request.deleteFiles,
        },
        fetchPolicy: 'network-only',
      })
      .pipe(
        map((result: Apollo.MutateResult<ResultType>) => {
          return result.data?.deleteDataRows || null;
        }),
      );
  }

  copyDataRows(request: CopyDataRowsRequest): Observable<Data | null> {
    type ResultType = { copyDataRows: Data };

    return this.apollo
      .mutate<ResultType>({
        mutation: gql`
          mutation CopyDataRows(
            $sourceTableId: String!
            $destinationTableId: String!
            $rowIds: [String!]!
            $mappingId: String
            $columnMappings: [RowCopyMappingColumnInput!]
          ) {
            copyDataRows(
              request: {
                sourceTableId: $sourceTableId
                destinationTableId: $destinationTableId
                rowIds: $rowIds
                mappingId: $mappingId
                columnMappings: $columnMappings
              }
            ) {
              projectId
              tableName
              rowsAffected
            }
          }
        `,
        variables: {
          sourceTableId: request.sourceTableId,
          destinationTableId: request.destinationTableId,
          rowIds: request.rowIds,
          mappingId: request.mappingId,
          columnMappings: request.columnMappings,
        },
        fetchPolicy: 'network-only',
      })
      .pipe(
        map((result) => {
          return result.data?.copyDataRows || null;
        }),
      );
  }

  createRowCopyMapping(
    request: CreateRowCopyMappingRequest,
  ): Observable<RowCopyMapping | null> {
    type ResultType = { createRowCopyMapping: RowCopyMapping };

    return this.apollo
      .mutate<ResultType>({
        mutation: gql`
          mutation CreateRowCopyMapping(
            $projectId: String!
            $name: String!
            $sourceTableId: String!
            $destinationTableId: String!
            $columns: [RowCopyMappingColumnInput!]!
          ) {
            createRowCopyMapping(
              request: {
                projectId: $projectId
                name: $name
                sourceTableId: $sourceTableId
                destinationTableId: $destinationTableId
                columns: $columns
              }
            ) {
              id
              name
              projectId
              sourceTableId
              destinationTableId
              columns {
                sourceColumnId
                destinationColumnId
              }
            }
          }
        `,
        variables: {
          projectId: request.projectId,
          name: request.name,
          sourceTableId: request.sourceTableId,
          destinationTableId: request.destinationTableId,
          columns: request.columns,
        },
        fetchPolicy: 'network-only',
      })
      .pipe(map((result) => result.data?.createRowCopyMapping || null));
  }

  updateRowCopyMapping(
    request: UpdateRowCopyMappingRequest,
  ): Observable<RowCopyMapping | null> {
    type ResultType = { updateRowCopyMapping: RowCopyMapping };

    return this.apollo
      .mutate<ResultType>({
        mutation: gql`
          mutation UpdateRowCopyMapping(
            $id: String!
            $name: String
            $columns: [RowCopyMappingColumnInput!]
          ) {
            updateRowCopyMapping(
              request: { id: $id, name: $name, columns: $columns }
            ) {
              id
              name
              projectId
              sourceTableId
              destinationTableId
              columns {
                sourceColumnId
                destinationColumnId
              }
            }
          }
        `,
        variables: {
          id: request.id,
          name: request.name,
          columns: request.columns,
        },
        fetchPolicy: 'network-only',
      })
      .pipe(map((result) => result.data?.updateRowCopyMapping || null));
  }

  deleteRowCopyMapping(
    request: DeleteRowCopyMappingRequest,
  ): Observable<RowCopyMapping | null> {
    type ResultType = { deleteRowCopyMapping: RowCopyMapping };

    return this.apollo
      .mutate<ResultType>({
        mutation: gql`
          mutation DeleteRowCopyMapping($id: String!) {
            deleteRowCopyMapping(request: { id: $id }) {
              id
            }
          }
        `,
        variables: {
          id: request.id,
        },
        fetchPolicy: 'network-only',
      })
      .pipe(map((result) => result.data?.deleteRowCopyMapping || null));
  }

  findAllRowCopyMappings(
    request: FindAllRowCopyMappingsRequest,
  ): Observable<RowCopyMapping[]> {
    type ResultType = { findAllRowCopyMappings: RowCopyMapping[] };

    const query = gql`
      query FindAllRowCopyMappings($where: RowCopyMappingsWhere) {
        findAllRowCopyMappings(request: { where: $where }) {
          id
          name
          projectId
          sourceTableId
          destinationTableId
          columns {
            sourceColumnId
            destinationColumnId
          }
        }
      }
    `;

    // This is re-run with the exact same variables within a single session
    // (e.g. picking the same destination table again right after saving a
    // mapping for it), so it needs a genuine one-shot fetch every time.
    // `watchQuery(...).valueChanges.pipe(take(1))` doesn't give that here —
    // Apollo's query deduplication can leave a repeat `watchQuery` for an
    // identical (query, variables) pair that never resolves, since the
    // previous subscriber already unsubscribed via `take(1)` the moment its
    // value arrived. `apollo.query()` always performs its own independent
    // fetch and reliably completes.
    return this.apollo
      .query<ResultType>({
        query,
        variables: { where: request.where },
        fetchPolicy: 'network-only',
      })
      .pipe(
        map(
          (result) =>
            (result.data?.findAllRowCopyMappings as
              | RowCopyMapping[]
              | undefined) ?? [],
        ),
      );
  }

  // ========================================================================
  // Row locks
  // ========================================================================

  lockDataRows(
    request: LockDataRowsRequest,
  ): Observable<LockDataRowsResponse | null> {
    type ResultType = { lockDataRows: LockDataRowsResponse };
    return this.apollo
      .mutate<ResultType>({
        mutation: gql`
          mutation LockDataRows(
            $tableId: String!
            $primaryKeyValues: [String!]!
            $reason: String
          ) {
            lockDataRows(
              request: {
                tableId: $tableId
                primaryKeyValues: $primaryKeyValues
                reason: $reason
              }
            ) {
              affected
            }
          }
        `,
        variables: {
          tableId: request.tableId,
          primaryKeyValues: request.primaryKeyValues,
          reason: request.reason,
        },
        fetchPolicy: 'network-only',
      })
      .pipe(map((result) => result.data?.lockDataRows || null));
  }

  unlockDataRows(
    request: UnlockDataRowsRequest,
  ): Observable<LockDataRowsResponse | null> {
    type ResultType = { unlockDataRows: LockDataRowsResponse };
    return this.apollo
      .mutate<ResultType>({
        mutation: gql`
          mutation UnlockDataRows(
            $tableId: String!
            $primaryKeyValues: [String!]!
          ) {
            unlockDataRows(
              request: {
                tableId: $tableId
                primaryKeyValues: $primaryKeyValues
              }
            ) {
              affected
            }
          }
        `,
        variables: {
          tableId: request.tableId,
          primaryKeyValues: request.primaryKeyValues,
        },
        fetchPolicy: 'network-only',
      })
      .pipe(map((result) => result.data?.unlockDataRows || null));
  }

  lockDataRowsByFilter(
    request: LockDataRowsByFilterRequest,
  ): Observable<LockDataRowsResponse | null> {
    type ResultType = { lockDataRowsByFilter: LockDataRowsResponse };
    return this.apollo
      .mutate<ResultType>({
        mutation: gql`
          mutation LockDataRowsByFilter(
            $tableId: String!
            $search: String
            $reason: String
          ) {
            lockDataRowsByFilter(
              request: { tableId: $tableId, search: $search, reason: $reason }
            ) {
              affected
            }
          }
        `,
        variables: {
          tableId: request.tableId,
          search: request.search,
          reason: request.reason,
        },
        fetchPolicy: 'network-only',
      })
      .pipe(map((result) => result.data?.lockDataRowsByFilter || null));
  }

  unlockDataRowsByFilter(
    request: UnlockDataRowsByFilterRequest,
  ): Observable<LockDataRowsResponse | null> {
    type ResultType = { unlockDataRowsByFilter: LockDataRowsResponse };
    return this.apollo
      .mutate<ResultType>({
        mutation: gql`
          mutation UnlockDataRowsByFilter($tableId: String!, $search: String) {
            unlockDataRowsByFilter(
              request: { tableId: $tableId, search: $search }
            ) {
              affected
            }
          }
        `,
        variables: {
          tableId: request.tableId,
          search: request.search,
        },
        fetchPolicy: 'network-only',
      })
      .pipe(map((result) => result.data?.unlockDataRowsByFilter || null));
  }

  findRowLocks(request: FindRowLocksRequest): Observable<RowLock[]> {
    type ResultType = { findRowLocks: RowLock[] };
    const networkStatus = this.store.selectSnapshot<boolean>(
      (state) => state.network.status,
    );
    const fetchPolicy = networkStatus ? 'network-only' : 'cache-only';
    const query = gql`
      query FindRowLocks($tableId: String!) {
        findRowLocks(request: { tableId: $tableId }) {
          id
          tableId
          primaryKeyValue
          lockedById
          lockedAt
          reason
        }
      }
    `;
    return this.apollo
      .watchQuery<ResultType>({
        query,
        variables: { tableId: request.tableId },
        fetchPolicy: <WatchQueryFetchPolicy>fetchPolicy,
      })
      .valueChanges.pipe(
        take(1),
        map(
          (result) =>
            (result.data?.findRowLocks as RowLock[] | undefined) ?? [],
        ),
      );
  }

  /*** helper methods used by components or stores ***/

  constructDataRow(
    columns: Column[],
    row: Record<string, unknown>,
  ): DataColumn[] {
    const dataColumns: DataColumn[] = [];

    columns.forEach((column) => {
      if (!column.autoIncrement) {
        const key = Object.keys(row).filter((x) => x === column.name)[0];
        let value = null;
        if (String(row[key]) !== 'null') {
          value = String(row[key]);

          if (column.dataType === DataType.FILE) {
            value = "'" + value + "'";
          }

          if (
            column.dataType === DataType.DATE ||
            column.dataType === DataType.TIMESTAMP
          ) {
            const parsed = moment(value);
            if (parsed.isValid()) {
              value = parsed.format('YYYY-MM-DDTHH:mm:ss');
            }
          }
        }

        dataColumns.push({
          columnId: column.id,
          value,
        });
      }
    });

    return dataColumns;
  }

  async convertFileToJSON(downloadUrl: string): Promise<unknown[]> {
    let text = await lastValueFrom(
      this.http.get(downloadUrl, { responseType: 'text' }),
    );

    const rows = text.split(/\r?\n/);
    if (rows.length > 1) {
      text = rows.slice(0, 2).join('\n');
    }

    return new Promise((resolve, reject) => {
      return Papa.parse(text, {
        header: true,
        skipEmptyLines: true,
        error: (e: Error) => reject(e),
        complete: (results) => resolve(results.data),
      });
    });
  }

  // TODO : Investigate type of data
  getColumnsFromData(data: unknown[]): Partial<Column>[] {
    const columns: Partial<Column>[] = [];
    const firstRow = data[0] as unknown;

    if (firstRow) {
      Object.keys(firstRow).forEach((key) => {
        const dataType = this.getDataType(firstRow, key);
        columns.push({
          name: key,
          dataType,
        });
      });
    } else throw 'File has no data';
    return columns;
  }

  private getDataType(data: any, key: string): DataType {
    // default to text
    if (!data[key]) return DataType.TEXT;

    if (Number(data[key])) {
      // if no decimal in value then cast to INT, else DOUBLE
      if (data[key].indexOf('.') === -1) return DataType.INT;
      return DataType.DOUBLE;
    }

    if (this.isBoolean(data[key])) {
      // this does not work for values like 'yes' or 'on'
      return DataType.BOOLEAN;
    }

    return DataType.TEXT;
  }

  private isBoolean(value: any): boolean {
    if (value === true || value === false) return true;
    if (typeof value === 'string') {
      if (value.toLowerCase() === 'false' || value.toLowerCase() === 'true')
        return true;
    }
    return false;
  }
}
